import React, { useContext, useEffect, useMemo, useState } from 'react';

import { Clear, CurrencyExchange, Replay } from '@mui/icons-material';
import { Alert, Box, Button, Dialog, Snackbar, Typography, useMediaQuery, useTheme } from '@mui/material';

import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useSession } from 'next-auth/react';
import NextLink from 'next/link';
import { bool, func, string } from 'prop-types';

import RESERVATION_STATUS from '../../constants/reservationStatus';
import { track } from '../../modules/segment';
import { AccountOrdersContext } from '../../providers/AccountOrders';
import ReservationContext from '../../providers/ReservationContext';
import Divider from '../shared/Divider';
import BabyTimeline from './BabyTimeline';
import ContextualActions from './ContextualActions';
import QuickActions from './QuickActions';
import getActionsData from './progressMenuData/actionsData';
import useQuickActionsData from './progressMenuData/quickActionsData';
import stepsData from './progressMenuData/stepsData';

dayjs.extend(isSameOrBefore);

const ADTLogo = '/svgs/cobranding/ADT.svg';

const ReferralDialog = ({ open, onClose, referralAccountId }) => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));

  const [clipboardButtonText, setClipboardButtonText] = useState('Copy link to clipboard');

  const resetButtonText = () => {
    setTimeout(() => setClipboardButtonText('Copy link to clipboard'), 250);
  };

  const referralLink = `${process.env.NEXT_PUBLIC_BOOKING_SITE_URL}/v2/book/service?utm_medium=CRM&utm_source=customer-referral&utm_content=owned:crm:referral:na:na:na:na&utm_campaign=${referralAccountId}&pc=GiveGet50`;

  const copyReferralLinkToClipboard = () => {
    if (navigator?.clipboard) {
      track('Dashboard - Customer Referral Link copied to clipboard');
      navigator.clipboard.writeText(referralLink);
      setClipboardButtonText('Copied to clipboard');
    }
  };

  const close = () => {
    resetButtonText();
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullScreen={Boolean(mobile)}>
      <Box maxWidth={380} minWidth={300}>
        <Box display='flex' justifyContent='flex-end' p={1} pb={0}>
          <Box display='flex' p={1} sx={{ '&:hover': { cursor: 'pointer' } }}>
            <Clear onClick={close} />
          </Box>
        </Box>
        <Box px={2} pb={4}>
          <Box pb={2}>
            <Typography variant='h4' textAlign='center'>
              Give $50, Get $50
            </Typography>
          </Box>
          <Divider />
          <Box pt={3} px={2}>
            <Typography variant='body1' textAlign='center'>
              When you refer a friend, they save $50, and you earn a $50 rebate when they complete their move.
            </Typography>
          </Box>
          <Box pt={3} px={2}>
            <Typography variant='body1' textAlign='center'>
              Use the button below to copy the link to your clipboard and send to your friends.
            </Typography>
          </Box>
          <Box pt={3} px={4}>
            <Button onClick={copyReferralLinkToClipboard}>{clipboardButtonText}</Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

ReferralDialog.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  referralAccountId: string.isRequired,
};

const ProgressMenu = () => {
  const theme = useTheme();
  const { permissions, orderV2Data } = useContext(ReservationContext);
  const { data: session, update } = useSession();
  const { data: accountOrders } = useContext(AccountOrdersContext);

  const [stage, setStage] = useState('');
  const [step, setStep] = useState(0);
  const [referralDialogOpen, setReferralDialogOpen] = useState(false);

  const [referralAccountId, setReferralAccountId] = useState(orderV2Data?.account_id);

  const [snackbarState, setSnackbarState] = useState({
    open: false,
    vertical: 'bottom',
    horizontal: 'center',
    message: '',
    severity: 'info',
  });

  const handleSnackbarClose = () => {
    setSnackbarState({
      ...snackbarState,
      open: false,
      message: '',
    });
  };

  const { invoice } = orderV2Data ?? {};

  const { allTransactions: transactions } = invoice ?? {};

  const actions = getActionsData({ transactions, status: orderV2Data?.status, jobs: orderV2Data?.jobs ?? [], permissions })
    .filter(({ context = [] }) => context.some(({ id = '' }) => id === stage))
    .sort(({ context: contextA = {} }, { context: contextB = {} }) => {
      const { priority: priorityA = 100 } = contextA.find(({ id = '' }) => id === stage);
      const { priority: priorityB = 100 } = contextB.find(({ id = '' }) => id === stage);

      return priorityA - priorityB;
    });

  const quickActionsData = useQuickActionsData();

  const quickActions = useMemo(
    () =>
      [
        ...(referralAccountId
          ? [
              {
                Icon: CurrencyExchange,
                label: 'Give $50, Get $50',
                description:
                  'Refer family or friends, they save $50, and you get a $50 rebate when they complete their move.',
                linkText: 'Refer now',
                stepIDs: ['movePrep', 'moveDay', 'postMove'],
                displayOnMobile: true,
                onClick: () => {
                  track('Dashboard - Customer Referral Dialog Opened');
                  setReferralDialogOpen(true);
                },
              },
              {
                id: 'ADT_Referral',
                Icon: ADTLogo,
                label: 'Get protection from the unpredictable',
                onClick: () => {
                  track('Dashboard - ADT Referral - Clicked');
                  let url = `${process.env.NEXT_PUBLIC_DASHBOARD_URL}api/proxy/referral/adt/?accountId=${referralAccountId}`;

                  if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'preview') {
                    url = `https://${process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL}/api/proxy/referral/adt/?accountId=${referralAccountId}`;
                  }

                  fetch(url, { method: 'POST' }).then(async (response) => {
                    if (!response.ok) {
                      track('Dashboard - ADT Referral - Failed');
                      return;
                    }

                    const referralResponse = await response.json();
                    if (referralResponse?.message?.startsWith('Success')) {
                      track('Dashboard - ADT Referral - Success');
                      setSnackbarState({
                        ...snackbarState,
                        open: true,
                        message: 'An ADT representative will be in touch',
                        severity: 'success',
                      });
                    } else {
                      track('Dashboard - ADT Referral - Unknown Response', { message: referralResponse?.message });
                      setSnackbarState({ ...snackbarState, open: true, message: 'Something went wrong', severity: 'error' });
                    }
                  });
                },
                description:
                  "Life can be unpredictable, but with ADT's 24/7 professional monitoring, it doesn't have to be.",
                linkText: 'Protect my home with ADT',
                stepIDs: ['movePrep', 'moveDay', 'postMove'],
                displayOnMobile: true,
              },
            ]
          : []),
        ...quickActionsData,
      ].filter(({ stepIDs = [] }) => stepIDs.includes(stage)),
    [referralAccountId, quickActionsData, stage],
  );

  useEffect(() => {
    if (orderV2Data?.account_id && !referralAccountId) {
      setReferralAccountId(orderV2Data.account_id);
    }
  }, [orderV2Data, referralAccountId]);

  useEffect(() => {
    if (stage) {
      setStep(stepsData.findIndex(({ id }) => id === stage));
    }
  }, [stage]);

  useEffect(() => {
    if (!orderV2Data) return;

    const reservationStartDate = dayjs(orderV2Data?.jobs?.[0].start_date_time);

    if (orderV2Data?.status === RESERVATION_STATUS.COMPLETED) {
      setStage('postMove');
    } else if (reservationStartDate.isSameOrBefore(dayjs(), 'day')) {
      setStage('moveDay');
    } else {
      setStage('movePrep');
    }
  }, [orderV2Data]);

  const updateTimeline = (selectedStage) => {
    setStage(selectedStage);
  };

  return (
    <>
      <ReferralDialog
        open={referralDialogOpen}
        onClose={() => setReferralDialogOpen(false)}
        referralAccountId={referralAccountId}
      />
      <Snackbar open={snackbarState.open} autoHideDuration={10000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarState.severity} variant='filled' sx={{ width: '100%' }}>
          {snackbarState.message}
        </Alert>
      </Snackbar>
      <Box display={{ xs: 'block', md: 'flex' }} alignItems='start' marginTop={{ xs: 0, md: 3.5 }}>
        <Box flexGrow={2}>
          <Typography display={{ xs: 'none', md: 'block' }} variant='h4' component='h2' sx={{ marginBottom: 2 }}>
            Timeline and actions
          </Typography>
          <Box
            padding={{ xs: 0, md: theme.spacing(6, 0) }}
            bgcolor={{ md: theme.palette.neutral.lightest }}
            borderRadius={{ xs: 0, md: '10px' }}
            sx={{
              border: {
                xs: 'none',
                md: `1px solid ${theme.palette.neutral.light}`,
              },
            }}
          >
            <BabyTimeline steps={stepsData} activeStep={step} onClick={updateTimeline} />
            <Box marginTop={4}>
              <ContextualActions actions={actions} />
            </Box>
          </Box>
        </Box>
        <Box flexGrow={1} maxWidth={{ md: 300 }} ml={{ md: 6 }}>
          <Typography display={{ xs: 'none', md: 'block' }} variant='h4' component='h2'>
            Add-ons and extras
          </Typography>
          <QuickActions quickActions={quickActions} />
        </Box>
        {session?.type === 'oauth' && session?.user?.email ? (
          <Box display={{ xs: 'block', md: 'none' }} my={2}>
            <Box mb={2}>
              <Typography variant='h5' textAlign='center'>
                Logged in as: {session?.user?.email}
              </Typography>
            </Box>
            {accountOrders?.length > 1 ? (
              <Box maxWidth={500} mx='auto' px={4}>
                <Button
                  LinkComponent={NextLink}
                  startIcon={<Replay />}
                  variant='contained'
                  href='/'
                  onClick={() => {
                    track('Dashboard - Mobile Home - Switch Order');
                    update({ action: 'switch_order' });
                  }}
                >
                  Switch Order
                </Button>
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </>
  );
};

export default ProgressMenu;
